import i18n from "../../i18n";
import {
    ManualPaymentInput,
    Transaction,
    TransactionFilters,
    TransactionPage,
    TransactionStats,
} from '../../model/payment/Transaction';
import { CommissionRecord, CommissionPayrollRow } from '../../model/teammember/Commission';

const API_URL = process.env.REACT_APP_PAYMENT_SERVICE_BASE_URL || '';

const COMMISSION_FIELDS = `
    id businessId teamMemberId serviceId appointmentId transactionId
    baseAmount rate amount currency createdOn
`;

const TRANSACTION_FIELDS = `
    id
    businessId
    appointmentId
    appointmentTotal
    eventId
    eventParticipantId
    giftCardCode
    clientId
    customerName
    amount
    currency
    discountAmount
    platformFee
    netAmount
    method
    status
    stripePaymentIntentId
    stripeChargeId
    description
    notes
    refundedAmount
    refundedAt
    createdBy
    createdOn
    updatedOn
    deleted
    deletedOn
    deletedBy
`;

async function gqlPost<T>(token: string, query: string, variables?: object): Promise<T> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) {
        const msg = json.errors.map((e: { message: string }) => e.message).join(', ');
        throw new Error(msg);
    }
    return json.data as T;
}

export async function getTransactions(
    businessId: string,
    filters: TransactionFilters,
    token: string
): Promise<TransactionPage> {
    const data = await gqlPost<{ getTransactions: TransactionPage }>(
        token,
        `query getTransactions($businessId: ID!, $filters: TransactionFilters) {
            getTransactions(businessId: $businessId, filters: $filters) {
                items { ${TRANSACTION_FIELDS} }
                total
                page
                perPage
            }
        }`,
        { businessId, filters }
    );
    return data.getTransactions;
}

export async function getTransactionStats(
    businessId: string,
    start: string,
    end: string,
    token: string
): Promise<TransactionStats> {
    const data = await gqlPost<{ getTransactionStats: TransactionStats }>(
        token,
        `query getTransactionStats($businessId: ID!, $start: String!, $end: String!) {
            getTransactionStats(businessId: $businessId, start: $start, end: $end) {
                grossTotal
                netTotal
                refundedTotal
                count
                averageTicket
                byMethod { method total count }
            }
        }`,
        { businessId, start, end }
    );
    return data.getTransactionStats;
}

export interface CheckoutSessionInput {
    businessId: string;
    appointmentId?: string | null;
    /** Snapshot of the appointment's total amount due — drives PARTIAL_PAID detection. */
    appointmentTotal?: number | null;
    clientId?: string | null;
    customerName?: string | null;
    customerEmail?: string | null;
    amount: number;
    currency?: string | null;
    discountAmount?: number | null;
    description?: string | null;
    successUrl: string;
    cancelUrl: string;
}

export interface StripeCheckoutResponse {
    url: string;
    sessionId: string;
    transactionId: string;
}

export async function createStripeCheckoutSession(
    input: CheckoutSessionInput,
    token: string
): Promise<StripeCheckoutResponse> {
    const data = await gqlPost<{ createStripeCheckoutSession: StripeCheckoutResponse }>(
        token,
        `mutation createStripeCheckoutSession($input: CheckoutSessionInput!) {
            createStripeCheckoutSession(input: $input) {
                url
                sessionId
                transactionId
            }
        }`,
        { input }
    );
    return data.createStripeCheckoutSession;
}

export async function recordManualPayment(
    input: ManualPaymentInput,
    token: string
): Promise<Transaction> {
    const data = await gqlPost<{ recordManualPayment: Transaction }>(
        token,
        `mutation recordManualPayment($input: ManualPaymentInput!) {
            recordManualPayment(input: $input) { ${TRANSACTION_FIELDS} }
        }`,
        { input }
    );
    return data.recordManualPayment;
}

/**
 * Mark a convênio receivable (PENDING CONVENIO transaction) as received —
 * flips it to SUCCEEDED so it counts as realized faturamento. Gated by
 * PAYMENTS (edit) on the backend.
 */
export async function markReceivableReceived(
    transactionId: string,
    token: string
): Promise<Transaction> {
    const data = await gqlPost<{ markReceivableReceived: Transaction }>(
        token,
        `mutation markReceivableReceived($transactionId: ID!) {
            markReceivableReceived(transactionId: $transactionId) { ${TRANSACTION_FIELDS} }
        }`,
        { transactionId }
    );
    return data.markReceivableReceived;
}

export async function refundTransaction(
    transactionId: string,
    amount: number | null,
    token: string
): Promise<Transaction> {
    const data = await gqlPost<{ refundTransaction: Transaction }>(
        token,
        `mutation refundTransaction($transactionId: ID!, $amount: Float) {
            refundTransaction(transactionId: $transactionId, amount: $amount) {
                ${TRANSACTION_FIELDS}
            }
        }`,
        { transactionId, amount }
    );
    return data.refundTransaction;
}

/** Edit payload for an existing manual transaction. */
export interface ManualPaymentUpdateInput {
    id: string;
    customerName?: string | null;
    amount?: number | null;
    discountAmount?: number | null;
    method?: string | null;
    description?: string | null;
    notes?: string | null;
}

export async function updateManualPayment(
    input: ManualPaymentUpdateInput,
    token: string
): Promise<Transaction> {
    const data = await gqlPost<{ updateManualPayment: Transaction }>(
        token,
        `mutation updateManualPayment($input: ManualPaymentUpdateInput!) {
            updateManualPayment(input: $input) { ${TRANSACTION_FIELDS} }
        }`,
        { input }
    );
    return data.updateManualPayment;
}

/**
 * Every transaction tied to a single appointment, newest first.
 * Used by the calendar Drawer's Payment tab to render the "already paid"
 * state instead of the empty split-payment form on a paid appointment.
 */
export async function getCommissionsByTeamMember(
    businessId: string,
    teamMemberId: string,
    token: string
): Promise<CommissionRecord[]> {
    const data = await gqlPost<{ getCommissionsByTeamMember: CommissionRecord[] }>(
        token,
        `query getCommissionsByTeamMember($businessId: ID!, $teamMemberId: ID!) {
            getCommissionsByTeamMember(businessId: $businessId, teamMemberId: $teamMemberId) { ${COMMISSION_FIELDS} }
        }`,
        { businessId, teamMemberId }
    );
    return data.getCommissionsByTeamMember ?? [];
}

export async function getCommissionsByBusiness(
    businessId: string,
    token: string
): Promise<CommissionRecord[]> {
    const data = await gqlPost<{ getCommissionsByBusiness: CommissionRecord[] }>(
        token,
        `query getCommissionsByBusiness($businessId: ID!) {
            getCommissionsByBusiness(businessId: $businessId) { ${COMMISSION_FIELDS} }
        }`,
        { businessId }
    );
    return data.getCommissionsByBusiness ?? [];
}

export async function getCommissionPayroll(
    businessId: string,
    start: string | null,
    end: string | null,
    token: string
): Promise<CommissionPayrollRow[]> {
    const data = await gqlPost<{ getCommissionPayroll: CommissionPayrollRow[] }>(
        token,
        `query getCommissionPayroll($businessId: ID!, $start: String, $end: String) {
            getCommissionPayroll(businessId: $businessId, start: $start, end: $end) {
                teamMemberId count totalAmount paidAmount pendingAmount currency
            }
        }`,
        { businessId, start: start || null, end: end || null }
    );
    return data.getCommissionPayroll ?? [];
}

export async function getCommissionDetails(
    businessId: string,
    start: string | null,
    end: string | null,
    teamMemberId: string | null,
    token: string
): Promise<CommissionRecord[]> {
    const data = await gqlPost<{ getCommissionDetails: CommissionRecord[] }>(
        token,
        `query getCommissionDetails($businessId: ID!, $start: String, $end: String, $teamMemberId: ID) {
            getCommissionDetails(businessId: $businessId, start: $start, end: $end, teamMemberId: $teamMemberId) {
                ${COMMISSION_FIELDS} paidAt paidBy
            }
        }`,
        { businessId, start: start || null, end: end || null, teamMemberId: teamMemberId || null }
    );
    return data.getCommissionDetails ?? [];
}

export async function markCommissionPaid(
    commissionId: string,
    paidDate: string | null,
    token: string
): Promise<boolean> {
    const data = await gqlPost<{ markCommissionPaid: boolean }>(
        token,
        `mutation markCommissionPaid($commissionId: ID!, $paidDate: String) {
            markCommissionPaid(commissionId: $commissionId, paidDate: $paidDate)
        }`,
        { commissionId, paidDate }
    );
    return !!data.markCommissionPaid;
}

export async function markCommissionsPaid(
    businessId: string,
    teamMemberId: string,
    start: string | null,
    end: string | null,
    paidDate: string,
    token: string
): Promise<number> {
    const data = await gqlPost<{ markCommissionsPaid: number }>(
        token,
        `mutation markCommissionsPaid($businessId: ID!, $teamMemberId: ID!, $start: String, $end: String, $paidDate: String) {
            markCommissionsPaid(businessId: $businessId, teamMemberId: $teamMemberId, start: $start, end: $end, paidDate: $paidDate)
        }`,
        { businessId, teamMemberId, start: start || null, end: end || null, paidDate }
    );
    return data.markCommissionsPaid ?? 0;
}

export async function getTransactionsByAppointment(
    appointmentId: string,
    token: string
): Promise<Transaction[]> {
    const data = await gqlPost<{ transactionsByAppointment: Transaction[] }>(
        token,
        `query transactionsByAppointment($appointmentId: ID!) {
            transactionsByAppointment(appointmentId: $appointmentId) {
                ${TRANSACTION_FIELDS}
            }
        }`,
        { appointmentId }
    );
    return data.transactionsByAppointment ?? [];
}

export async function deleteManualPayment(
    transactionId: string,
    token: string
): Promise<boolean> {
    const data = await gqlPost<{ deleteManualPayment: boolean }>(
        token,
        `mutation deleteManualPayment($transactionId: ID!) {
            deleteManualPayment(transactionId: $transactionId)
        }`,
        { transactionId }
    );
    return !!data.deleteManualPayment;
}
